import React, {useEffect, useMemo, useState} from 'react';
import block from 'bem-cn-lite';
import {Link} from 'react-router-dom';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {ArrowUpArrowDown, CaretDown, CaretUp} from '@gravity-ui/icons';
import {useMediaQuery} from 'react-responsive';
import type {SuggestItem} from '@features/search/interfaces/SuggestItem';
import {useVisitedBrandSuggestions} from '@features/search/hooks/useVisitedBrandSuggestions';
import {getSearchUrl} from '@utils';
import {BrandSuggestionsMobileSorting} from './BrandSuggestionsMobileSorting';
import {
    BRAND_SUGGESTION_COLUMNS,
    BrandSuggestionSortOption,
    BrandSuggestionSortProperty,
    SortDirection,
    applyBrandSuggestionSort,
    getDefaultBrandSuggestionSortOptions,
    getNextBrandSuggestionSort,
    isDefaultBrandSuggestionSort,
    sortBrandSuggestions
} from './sortBrandSuggestions';

import './BrandSuggestions.scss';

const b = block('brand-suggestions');

export interface BrandSuggestionsProps {
    items: SuggestItem[];
    article: string;
    useAnalogs: boolean;
}

export const BrandSuggestions: React.FC<BrandSuggestionsProps> = ({items, article, useAnalogs}) => {
    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});
    const {isVisited, markVisited} = useVisitedBrandSuggestions();
    const [sortOptions, setSortOptions] = useState<BrandSuggestionSortOption[]>(
        getDefaultBrandSuggestionSortOptions
    );
    const [isSortingOpen, setIsSortingOpen] = useState(false);

    useEffect(() => {
        setSortOptions(getDefaultBrandSuggestionSortOptions());
        setIsSortingOpen(false);
    }, [article]);

    const sortedItems = useMemo(
        () => sortBrandSuggestions(items, sortOptions),
        [items, sortOptions]
    );

    function handleColumnSort(property: BrandSuggestionSortProperty) {
        setSortOptions((prev) => getNextBrandSuggestionSort(prev, property));
    }

    function handlePresetSort(property: BrandSuggestionSortProperty, direction: SortDirection) {
        setSortOptions((prev) => applyBrandSuggestionSort(prev, property, direction));
    }

    return (
        <div className={b()}>
            <div className={b('title-row')}>
                <Text
                    className={b('title')}
                    variant={isSmallScreen ? 'subheader-2' : 'subheader-3'}
                >
                    Результаты поиска бренда
                </Text>
                {isSmallScreen ? (
                    <Button
                        view="normal"
                        size="m"
                        aria-label="Сортировка"
                        className={b('sort-button')}
                        selected={!isDefaultBrandSuggestionSort(sortOptions)}
                        onClick={() => setIsSortingOpen(true)}
                    >
                        <Icon data={ArrowUpArrowDown} />
                    </Button>
                ) : null}
            </div>

            {isSmallScreen ? (
                <div className={b('items')}>
                    {sortedItems.map((item) => (
                        <Link
                            key={item.name}
                            to={getSearchUrl(article, item.name, useAnalogs)}
                            className={b('mobile-item', {visited: isVisited(article, item.name)})}
                            onClick={() => markVisited(article, item.name)}
                            onAuxClick={() => markVisited(article, item.name)}
                        >
                            <div translate="no">
                                <Text variant="body-2">{item.name}</Text>
                            </div>
                            <Text color="secondary" variant="body-2">
                                {item.articleName}
                            </Text>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className={b('items')}>
                    <div className={b('header')}>
                        {BRAND_SUGGESTION_COLUMNS.map((column) => {
                            if (!column.sortable) {
                                return (
                                    <div
                                        key={column.property}
                                        className={b('header-cell', {static: true})}
                                    >
                                        <span>{column.title}</span>
                                    </div>
                                );
                            }

                            const sortOption = sortOptions.find(
                                (option) => option.property === column.property
                            );

                            if (!sortOption) {
                                return null;
                            }

                            return (
                                <SortableHeaderCell
                                    key={column.property}
                                    title={column.title}
                                    sortOption={sortOption}
                                    onSort={() => handleColumnSort(column.property)}
                                />
                            );
                        })}
                    </div>
                    {sortedItems.map((item) => (
                        <Link
                            key={item.name}
                            to={getSearchUrl(article, item.name, useAnalogs)}
                            className={b('item', {visited: isVisited(article, item.name)})}
                            onClick={() => markVisited(article, item.name)}
                            onAuxClick={() => markVisited(article, item.name)}
                        >
                            <div translate="no">{item.name}</div>
                            <div>
                                <span className={b('article')}>{article}</span>
                            </div>
                            <Text
                                color="complementary"
                                variant="body-2"
                                style={{padding: '10px 12px'}}
                            >
                                {item.articleName}
                            </Text>
                        </Link>
                    ))}
                </div>
            )}

            <BrandSuggestionsMobileSorting
                isOpen={isSortingOpen}
                onClose={() => setIsSortingOpen(false)}
                sortOptions={sortOptions}
                onSortChanged={handlePresetSort}
            />
        </div>
    );
};

interface SortableHeaderCellProps {
    title: string;
    sortOption: BrandSuggestionSortOption;
    onSort: () => void;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({title, sortOption, onSort}) => {
    let sortLabel = 'без сортировки';

    if (sortOption.isActive) {
        sortLabel =
            sortOption.direction === 'asc' ? 'сортировка A-Z (А-Я)' : 'сортировка Z-A (Я-А)';
    }

    return (
        <div
            className={b('header-cell')}
            role="button"
            tabIndex={0}
            aria-label={`${title}: ${sortLabel}`}
            aria-sort={getAriaSort(sortOption)}
            onClick={onSort}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSort();
                }
            }}
        >
            <span>{title}</span>
            <SortCaret option={sortOption} />
        </div>
    );
};

function getAriaSort(option: BrandSuggestionSortOption): 'ascending' | 'descending' | 'none' {
    if (!option.isActive) {
        return 'none';
    }

    return option.direction === 'asc' ? 'ascending' : 'descending';
}

function SortCaret({option}: {option: BrandSuggestionSortOption}) {
    const showAscending = option.isActive && option.direction === 'asc';
    const CaretIcon = showAscending ? CaretUp : CaretDown;

    return <CaretIcon className={b('caret', {active: option.isActive})} />;
}
